import React, { useState, useEffect } from "react";
import moment from "moment";
import toast from "react-hot-toast";
import { getUserData } from "./const";
import Pagination from "../components/Pagination";
import { useButtonTableStyle } from "../utils/useButtonTableStyle";
import { createErpSocket } from "../utils/socketIo";
import { listDailyPrices, createDailyPrice } from "../erpClient";
import { formatIndianRupee } from "../utils/formatLiveRate";

const ITEMS_PER_PAGE = 10;

const rateFrom24 = (gold24, carat) => {
  const base = parseFloat(gold24);
  return Number.isFinite(base) && base > 0 ? (base * carat) / 24 : 0;
};

const displayGold22 = (price) =>
  formatIndianRupee(price.goldRate22 ?? rateFrom24(price.goldRate, 22));

const displayGold18 = (price) =>
  formatIndianRupee(price.goldRate18 ?? rateFrom24(price.goldRate, 18));

const displayGold9 = (price) =>
  formatIndianRupee(price.goldRate9 ?? rateFrom24(price.goldRate, 9));

const displayRose18 = (price) =>
  formatIndianRupee(
    price.roseGoldRate18 ?? price.goldRate18 ?? rateFrom24(price.goldRate, 18),
  );

const displayRose9 = (price) =>
  formatIndianRupee(
    price.roseGoldRate9 ?? price.goldRate9 ?? rateFrom24(price.goldRate, 9),
  );

const toInputValue = (value) => {
  if (value === null || value === undefined || value === "") return "";
  return String(value);
};

const formatPriceDateTime = (dateStr) => {
  if (!dateStr) return "-";
  const m = moment(dateStr);
  return m.isValid() ? m.format("DD/MM/YYYY | hh:mm A") : String(dateStr);
};

const resolveLastEntryRates = (price) => {
  if (!price) return null;
  const gold24 = price["24_carat_gold"] ?? price.goldRate ?? "";
  const silver = price["silver"] ?? price.silverRate ?? "";
  const gold22 =
    price["22_carat_gold"] ??
    price.goldRate22 ??
    (gold24 !== "" && !isNaN(gold24) && parseFloat(gold24) > 0
      ? ((parseFloat(gold24) * 22) / 24).toFixed(2)
      : "");
  const gold18 =
    price["18_carat_gold"] ??
    price.goldRate18 ??
    (gold24 !== "" && !isNaN(gold24) && parseFloat(gold24) > 0
      ? ((parseFloat(gold24) * 18) / 24).toFixed(2)
      : "");
  const gold9 =
    price["9_carat_gold"] ??
    price.goldRate9 ??
    (gold24 !== "" && !isNaN(gold24) && parseFloat(gold24) > 0
      ? ((parseFloat(gold24) * 9) / 24).toFixed(2)
      : "");
  const rose18 =
    price["18_carat_rose_gold"] ??
    price.roseGoldRate18 ??
    gold18 ??
    (gold24 !== "" && !isNaN(gold24) && parseFloat(gold24) > 0
      ? ((parseFloat(gold24) * 18) / 24).toFixed(2)
      : "");
  const rose9 =
    price["9_carat_rose_gold"] ??
    price.rose_gold_9 ??
    price.roseGoldRate9 ??
    gold9 ??
    (gold24 !== "" && !isNaN(gold24) && parseFloat(gold24) > 0
      ? ((parseFloat(gold24) * 9) / 24).toFixed(2)
      : "");

  return { gold24, silver, gold22, gold18, gold9, rose18, rose9 };
};

function DailyPrice() {
  const { buttonClass, cardClass, tableClass, buttonTableStyle } =
    useButtonTableStyle();
  const [dailyPrices, setDailyPrices] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(ITEMS_PER_PAGE);
  const [searchQuery, setSearchQuery] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [todayRateExists, setTodayRateExists] = useState(false);
  const [currentPrice, setCurrentPrice] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Add Daily Price form states
  const [goldRate, setGoldRate] = useState("");
  const [silverRate, setSilverRate] = useState("");
  const [goldRate22, setGoldRate22] = useState("");
  const [goldRate18, setGoldRate18] = useState("");
  const [goldRate9, setGoldRate9] = useState("");
  const [roseGoldRate18, setRoseGoldRate18] = useState("");
  const [roseGoldRate9, setRoseGoldRate9] = useState("");
  const [socket, setSocket] = useState(null);
  const userData = getUserData();

  const populateFormFromLastEntry = (price) => {
    const rates = resolveLastEntryRates(price);
    if (!rates) return;
    setGoldRate(toInputValue(rates.gold24));
    setSilverRate(toInputValue(rates.silver));
    setGoldRate22(toInputValue(rates.gold22));
    setGoldRate18(toInputValue(rates.gold18));
    setGoldRate9(toInputValue(rates.gold9));
    setRoseGoldRate18(toInputValue(rates.rose18));
    setRoseGoldRate9(toInputValue(rates.rose9));
  };

  // Removed separate last_entry API. We derive last entry from the show API response.

  const fetchDailyPrices = async () => {
    setIsLoading(true);
    try {
      const result = await listDailyPrices();

      if (result?.success && result.data) {
        const list = Array.isArray(result.data.daily_prices)
          ? result.data.daily_prices
          : [];
        const lastArr = Array.isArray(result.data.last_entry)
          ? result.data.last_entry
          : [];

        const fetchedPrices = list.map((price) => {
          const gold24 = price["24_carat_gold"] ?? "0";
          const silver = price["silver"] ?? "0";
          const gold22 =
            price["22_carat_gold"] ??
            (gold24 && !isNaN(gold24) && parseFloat(gold24) > 0
              ? ((parseFloat(gold24) * 22) / 24).toFixed(2)
              : "0");
          const gold18 =
            price["18_carat_gold"] ??
            (gold24 && !isNaN(gold24) && parseFloat(gold24) > 0
              ? ((parseFloat(gold24) * 18) / 24).toFixed(2)
              : "0");
          const gold9 =
            price["9_carat_gold"] ??
            (gold24 && !isNaN(gold24) && parseFloat(gold24) > 0
              ? ((parseFloat(gold24) * 9) / 24).toFixed(2)
              : "0");
          const rose18 = price["18_carat_rose_gold"] ?? gold18;
          const rose9 =
            price["9_carat_rose_gold"] ?? price.rose_gold_9 ?? gold9;

          return {
            id: price.id,
            date: price.date,
            byUserName: price.by_user_name ?? "",
            goldRate: gold24,
            silverRate: silver,
            goldRate22: gold22,
            goldRate18: gold18,
            goldRate9: gold9,
            roseGoldRate18: rose18,
            roseGoldRate9: rose9,
          };
        });

        fetchedPrices.sort((a, b) => moment(b.date).diff(moment(a.date)));
        setDailyPrices(fetchedPrices);

        if (lastArr.length > 0) {
          const price = lastArr[0];
          const rates = resolveLastEntryRates(price);
          const { gold24, silver, gold22, gold18, gold9, rose18, rose9 } =
            rates;

          const lastEntryDate = moment(price.date).format("YYYY-MM-DD");
          const today = moment().format("YYYY-MM-DD");
          setTodayRateExists(lastEntryDate === today);

          setCurrentPrice({
            date: price.date,
            byUserName: price.by_user_name ?? "",
            goldRate: gold24,
            silverRate: silver,
            goldRate22: gold22,
            goldRate18: gold18,
            goldRate9: gold9,
            roseGoldRate18: rose18,
            roseGoldRate9: rose9,
            isToday: lastEntryDate === today,
          });
          populateFormFromLastEntry(price);
        }
      }
    } catch (error) {
      console.error("Error fetching daily prices:", error);
      const message =
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch daily prices";
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const guardKey = "DailyPrice_fetch_guard";
    const lastTs = Number(sessionStorage.getItem(guardKey) || 0);
    const nowTs = Date.now();
    if (nowTs - lastTs < 500) {
      return;
    }
    sessionStorage.setItem(guardKey, String(nowTs));
    fetchDailyPrices();
  }, []);

  // Listen for API URL changes and refetch data
  useEffect(() => {
    const handleApiUrlChange = (event) => {
      console.log(
        "API URL changed in DailyPrice, refetching daily prices...",
        event.detail,
      );
      fetchDailyPrices();
    };

    window.addEventListener("apiUrlChanged", handleApiUrlChange);

    return () => {
      window.removeEventListener("apiUrlChanged", handleApiUrlChange);
    };
  }, []);

  // Socket.IO listener for real-time updates (create only)
  useEffect(() => {
    const newSocket = createErpSocket();
    setSocket(newSocket);

    // Connection event logging
    newSocket.on("connect", () => {
      // console.log("DailyPrice: Connected to socket server", newSocket.id);
    });

    newSocket.on("disconnect", (reason) => {
      // console.log("DailyPrice: Disconnected from socket server:", reason);
    });

    newSocket.on("connect_error", (error) => {
      console.error("DailyPrice: Socket connection error:", error);
    });

    newSocket.on("daily_price_created", (data) => {
      // console.log("Socket data received:", data);
      if (!data?.daily_price) return;

      const newDailyPrice = {
        id: data.daily_price.id,
        date: data.daily_price.date || moment().format("YYYY-MM-DD HH:mm:ss"),
        byUserName: data.daily_price.by_user_name ?? "",
        goldRate:
          data.daily_price["24_carat_gold"] ||
          data.daily_price.gold_24_carat ||
          "0",
        goldRate22:
          data.daily_price["22_carat_gold"] ||
          data.daily_price.gold_22_carat ||
          "0",
        goldRate18:
          data.daily_price["18_carat_gold"] ||
          data.daily_price.gold_18_carat ||
          "0",
        goldRate9:
          data.daily_price["9_carat_gold"] ||
          data.daily_price.gold_9_carat ||
          "0",
        roseGoldRate18:
          data.daily_price["18_carat_rose_gold"] ||
          data.daily_price.rose_gold_18_carat ||
          "0",
        roseGoldRate9:
          data.daily_price["9_carat_rose_gold"] ||
          data.daily_price.rose_gold_9 ||
          data.daily_price.rose_gold_9_carat ||
          "0",
        silverRate: data.daily_price.silver || "0",
      };

      // console.log("Processed daily price:", newDailyPrice);

      setDailyPrices((prev) => {
        // Check if daily price already exists (avoid duplicates)
        const exists = prev.some((price) => price.id === newDailyPrice.id);
        if (exists) {
          return prev.map((price) =>
            price.id === newDailyPrice.id ? newDailyPrice : price,
          );
        }
        // Add new daily price at the beginning (newest first)
        return [newDailyPrice, ...prev].sort((a, b) => {
          // Sort by date descending, then by id descending
          const dateComparison = moment(b.date).diff(moment(a.date));
          return dateComparison !== 0 ? dateComparison : b.id - a.id;
        });
      });

      // Update current price if it's today's rate
      const today = moment().format("YYYY-MM-DD");
      if (moment(newDailyPrice.date).format("YYYY-MM-DD") === today) {
        setCurrentPrice({
          ...newDailyPrice,
          isToday: true,
        });
        setTodayRateExists(true);
      }
      populateFormFromLastEntry(newDailyPrice);
    });

    return () => {
      newSocket.off("daily_price_created");
      newSocket.disconnect();
    };
  }, []);

  // Function to clear all form fields
  const clearFormFields = () => {
    setGoldRate("");
    setSilverRate("");
    setGoldRate22("");
    setGoldRate18("");
    setGoldRate9("");
    setRoseGoldRate18("");
    setRoseGoldRate9("");
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    try {
      const result = await createDailyPrice({
        gold24: goldRate,
        gold22: goldRate22,
        gold18: goldRate18,
        gold9: goldRate9,
        gold18Rose: roseGoldRate18,
        gold9Rose: roseGoldRate9,
        silver: silverRate,
      });

      if (result?.success) {
        toast.success("Daily price added successfully");
        // Prefer updating local state from API response (socket will also update)
        const dp = result.data?.daily_price || result.data || null;
        if (dp && dp.id !== undefined && dp.id !== null) {
          const newDailyPrice = {
            id: dp.id,
            date: dp.date || moment().format("YYYY-MM-DD HH:mm:ss"),
            byUserName:
              dp.by_user_name ?? userData?.name ?? userData?.user_name ?? "",
            goldRate: dp["24_carat_gold"] ?? goldRate ?? "0",
            goldRate22: dp["22_carat_gold"] ?? goldRate22 ?? "0",
            goldRate18: dp["18_carat_gold"] ?? goldRate18 ?? "0",
            goldRate9: dp["9_carat_gold"] ?? goldRate9 ?? "0",
            roseGoldRate18:
              dp["18_carat_rose_gold"] ??
              dp.rose_gold_18 ??
              roseGoldRate18 ??
              "0",
            roseGoldRate9:
              dp["9_carat_rose_gold"] ??
              dp.rose_gold_9 ??
              roseGoldRate9 ??
              "0",
            silverRate: dp.silver ?? silverRate ?? "0",
          };

          setDailyPrices((prev) => {
            const exists = prev.some((p) => p.id === newDailyPrice.id);
            const merged = exists
              ? prev.map((p) => (p.id === newDailyPrice.id ? newDailyPrice : p))
              : [newDailyPrice, ...prev];
            return merged.sort((a, b) => {
              const dateComparison = moment(b.date).diff(moment(a.date));
              return dateComparison !== 0 ? dateComparison : b.id - a.id;
            });
          });

          const today = moment().format("YYYY-MM-DD");
          if (moment(newDailyPrice.date).format("YYYY-MM-DD") === today) {
            setCurrentPrice({ ...newDailyPrice, isToday: true });
            setTodayRateExists(true);
          }
          populateFormFromLastEntry(newDailyPrice);
        }
      } else {
        toast.error(result?.message || "Failed to add daily price");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      const message =
        error.response?.data?.message ||
        error.message ||
        "Failed to add daily price";
      toast.error(message);
    }
  };

  // Input validation for numbers and decimal point only
  const handleNumberInput = (value, setter) => {
    const regex = /^\d*\.?\d*$/;
    if (value === "" || regex.test(value)) {
      setter(value);
    }
  };

  // Filtering based on search
  const filteredPrices = dailyPrices.filter((price) => {
    const dateStr = price.date ?? "";
    const formatted = formatPriceDateTime(price.date);
    return (
      dateStr.includes(searchQuery) ||
      formatted.toLowerCase().includes(searchQuery.toLowerCase()) ||
      moment(price.date).format("DD-MM-YYYY").includes(searchQuery) ||
      moment(price.date).format("DD/MM/YYYY").includes(searchQuery)
    );
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredPrices.length / itemsPerPage);
  const displayedPrices = filteredPrices.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );
  const handleItemsPerPageChange = (newSize) => {
    setItemsPerPage(newSize);
  };

  useEffect(() => {
    if (totalPages === 0 && currentPage !== 1) {
      setCurrentPage(1);
    } else if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  // Close modal and reset form
  const handleCloseModal = () => {
    setShowAddModal(false);
    setGoldRate("");
    setSilverRate("");
    setGoldRate22("");
    setGoldRate18("");
    setGoldRate9("");
    setRoseGoldRate18("");
    setRoseGoldRate9("");
  };

  return (
    <div className="body-wrapper">
      <div className="container-fluid">
        {/* Form Container */}
        <div
          className={`card w-100 position-relative overflow-hidden mb-4 ${cardClass}`}
        >
          <div className="px-4 py-3 border-bottom d-flex justify-content-between align-items-center">
            <h4 className="card-title mb-0">Add Metal Daily Price</h4>
          </div>

          <div className="card-body p-4">
            <form noValidate="" onSubmit={handleAddSubmit}>
              <div className="row g-2 align-items-end flex-nowrap">
                <div className="col">
                  <label className="form-label text-nowrap">24C Gold</label>
                  <input
                    type="text"
                    className={`form-control ${buttonTableStyle === "square" ? "rounded-0" : ""
                      }`}
                    placeholder="Enter Rate"
                    value={goldRate}
                    onChange={(e) =>
                      handleNumberInput(e.target.value, setGoldRate)
                    }
                  />
                </div>

                <div className="col">
                  <label className="form-label text-nowrap">22C Gold</label>
                  <input
                    type="text"
                    className={`form-control ${buttonTableStyle === "square" ? "rounded-0" : ""
                      }`}
                    placeholder="Enter Rate"
                    value={goldRate22}
                    onChange={(e) =>
                      handleNumberInput(e.target.value, setGoldRate22)
                    }
                  />
                </div>

                <div className="col">
                  <label className="form-label text-nowrap">18C Gold</label>
                  <input
                    type="text"
                    className={`form-control ${buttonTableStyle === "square" ? "rounded-0" : ""
                      }`}
                    placeholder="Enter Rate"
                    value={goldRate18}
                    onChange={(e) =>
                      handleNumberInput(e.target.value, setGoldRate18)
                    }
                  />
                </div>

                <div className="col">
                  <label className="form-label text-nowrap">18C Rose Gold</label>
                  <input
                    type="text"
                    className={`form-control ${buttonTableStyle === "square" ? "rounded-0" : ""
                      }`}
                    placeholder="Enter Rate"
                    value={roseGoldRate18}
                    onChange={(e) =>
                      handleNumberInput(e.target.value, setRoseGoldRate18)
                    }
                  />
                </div>

                <div className="col">
                  <label className="form-label text-nowrap">9C Gold</label>
                  <input
                    type="text"
                    className={`form-control ${buttonTableStyle === "square" ? "rounded-0" : ""
                      }`}
                    placeholder="Enter Rate"
                    value={goldRate9}
                    onChange={(e) =>
                      handleNumberInput(e.target.value, setGoldRate9)
                    }
                  />
                </div>

                <div className="col">
                  <label className="form-label text-nowrap">9C Rose Gold</label>
                  <input
                    type="text"
                    className={`form-control ${buttonTableStyle === "square" ? "rounded-0" : ""
                      }`}
                    placeholder="Enter Rate"
                    value={roseGoldRate9}
                    onChange={(e) =>
                      handleNumberInput(e.target.value, setRoseGoldRate9)
                    }
                  />
                </div>

                <div className="col">
                  <label className="form-label text-nowrap">Silver</label>
                  <input
                    type="text"
                    className={`form-control ${buttonTableStyle === "square" ? "rounded-0" : ""
                      }`}
                    placeholder="Enter Rate"
                    value={silverRate}
                    onChange={(e) =>
                      handleNumberInput(e.target.value, setSilverRate)
                    }
                  />
                </div>
              </div>
              <div className="d-flex justify-content-end mt-3">
                <button
                  type="submit"
                  className={`btn btn-primary px-4 ${buttonClass}`}
                >
                  Submit
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Current Price Display */}
        {currentPrice && (
          <div
            className={`card w-100 position-relative overflow-hidden mb-4 ${cardClass}`}
          >
            <div className="px-4 py-3 border-bottom d-flex flex-wrap justify-content-between align-items-center gap-2">
              <h4 className="card-title mb-0">
                {currentPrice.isToday
                  ? "Today's Price"
                  : "Current Price"}
              </h4>
              <div
                className="fs-4"
                style={{
                  color: "var(--bs-card-title-color, var(--bs-heading-color))",
                }}
              >
                <span className="me-3">
                  <strong>Updated At:</strong>{" "}
                  {formatPriceDateTime(currentPrice.date)}
                </span>
                <span>
                  <strong>Updated By:</strong>{" "}
                  {currentPrice.byUserName || "-"}
                </span>
              </div>
            </div>
            <div className="card-body p-4">
              <div className="row g-2 flex-nowrap">
                <div className="col">
                  <div
                    className={`text-center p-3 bg-warning bg-opacity-10 ${buttonTableStyle === "square" ? "rounded-0" : "rounded"
                      }`}
                  >
                    <h6 className="text-dark mb-2 text-nowrap">24C Gold</h6>
                    <h4 className="mb-0">
                      {formatIndianRupee(currentPrice.goldRate || 0)}
                    </h4>
                  </div>
                </div>
                <div className="col">
                  <div
                    className={`text-center p-3 bg-warning bg-opacity-10 ${buttonTableStyle === "square" ? "rounded-0" : "rounded"
                      }`}
                  >
                    <h6 className="text-dark mb-2 text-nowrap">22C Gold</h6>
                    <h4 className="mb-0">{displayGold22(currentPrice)}</h4>
                  </div>
                </div>
                <div className="col">
                  <div
                    className={`text-center p-3 bg-warning bg-opacity-10 ${buttonTableStyle === "square" ? "rounded-0" : "rounded"
                      }`}
                  >
                    <h6 className="text-dark mb-2 text-nowrap">18C Gold</h6>
                    <h4 className="mb-0">{displayGold18(currentPrice)}</h4>
                  </div>
                </div>
                <div className="col">
                  <div
                    className={`text-center p-3 bg-warning bg-opacity-10 ${buttonTableStyle === "square" ? "rounded-0" : "rounded"
                      }`}
                  >
                    <h6 className="text-dark mb-2 text-nowrap">18C Rose Gold</h6>
                    <h4 className="mb-0">{displayRose18(currentPrice)}</h4>
                  </div>
                </div>
                <div className="col">
                  <div
                    className={`text-center p-3 bg-warning bg-opacity-10 ${buttonTableStyle === "square" ? "rounded-0" : "rounded"
                      }`}
                  >
                    <h6 className="text-dark mb-2 text-nowrap">9C Gold</h6>
                    <h4 className="mb-0">{displayGold9(currentPrice)}</h4>
                  </div>
                </div>
                <div className="col">
                  <div
                    className={`text-center p-3 bg-warning bg-opacity-10 ${buttonTableStyle === "square" ? "rounded-0" : "rounded"
                      }`}
                  >
                    <h6 className="text-dark mb-2 text-nowrap">9C Rose Gold</h6>
                    <h4 className="mb-0">{displayRose9(currentPrice)}</h4>
                  </div>
                </div>
                <div className="col">
                  <div
                    className={`text-center p-3 bg-info bg-opacity-10 ${buttonTableStyle === "square" ? "rounded-0" : "rounded"
                      }`}
                  >
                    <h6 className="text-dark mb-2 text-nowrap">Silver</h6>
                    <h4 className="text-info mb-0">
                      {formatIndianRupee(currentPrice.silverRate || 0)}
                    </h4>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Table Container */}
        <div
          className={`card w-100 position-relative overflow-hidden ${cardClass}`}
        >
          <div className="card-body p-4">
            {/* Table */}
            <div className={`table-responsive mb-4 border ${cardClass}`}>
              <table className={`table mb-0 align-middle ${tableClass}`}>
                <thead className="text-dark fs-4">
                  <tr>
                    <th>Date & Time</th>
                    <th>24C Gold</th>
                    <th>22C Gold</th>
                    <th>18C Gold</th>
                    <th>18C Rose Gold</th>
                    <th>9C Gold</th>
                    <th>9C Rose Gold</th>
                    <th>Silver</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td colSpan="8" className="text-center">
                        Loading...
                      </td>
                    </tr>
                  ) : displayedPrices.length > 0 ? (
                    displayedPrices.map((price) => (
                      <tr key={price.id}>
                        <td>
                          {formatPriceDateTime(price.date)}
                        </td>
                        <td>
                          {formatIndianRupee(price.goldRate || 0)}
                        </td>
                        <td>
                          {displayGold22(price)}
                        </td>
                        <td>
                          {displayGold18(price)}
                        </td>
                        <td>
                          {displayRose18(price)}
                        </td>
                        <td>
                          {displayGold9(price)}
                        </td>
                        <td>
                          {displayRose9(price)}
                        </td>
                        <td>
                          {formatIndianRupee(price.silverRate || 0)}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="8" className="text-center">
                        No prices found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <Pagination
              totalItems={filteredPrices.length}
              itemsPerPage={itemsPerPage}
              currentPage={currentPage}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={handleItemsPerPageChange}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default DailyPrice;
